import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Send, 
  Loader2, 
  Bot,
  Wrench,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { formatTimestamp } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Message, ToolCall, ToolResult } from '@/types';

export function Chat() {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<(Message & { isStreaming?: boolean })[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [newSessionPlatform, setNewSessionPlatform] = useState('cli');
  const [newSessionChatId, setNewSessionChatId] = useState('');
  const [newSessionUserId, setNewSessionUserId] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (sessionId) {
      loadMessages();
    } else {
      setShowCreateSession(true);
    }
  }, [sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadMessages = async () => {
    if (!sessionId) return;
    try {
      setLoading(true);
      const res = await apiClient.getMessages(sessionId, { limit: 100 });
      // Reverse to show oldest first
      setMessages((res.messages || []).reverse());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    if (!sessionId) {
      await createSession();
      if (!sessionId) return;
    }

    const userMessage = input;
    setInput('');
    setLoading(true);
    setStreaming(true);
    setError(null);

    // Optimistically add user message
    const tempUserMessage: Message & { isStreaming: boolean } = {
      id: Date.now(),
      session_id: sessionId!,
      role: 'user',
      content: userMessage,
      created_at: Date.now(),
      isStreaming: false,
    };
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      const response = await apiClient.chat(sessionId!, userMessage);
      
      if (response.status === 'completed') {
        // Add assistant message
        const assistantMessage: Message = {
          id: Date.now() + 1,
          session_id: sessionId!,
          role: 'assistant',
          content: response.response,
          tool_calls: response.tool_calls,
          created_at: Date.now(),
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        // If there were tool calls, add tool results
        if (response.tool_results) {
          response.tool_results.forEach((toolResult, index) => {
            const toolMessage: Message = {
              id: Date.now() + 2 + index,
              session_id: sessionId!,
              role: 'tool',
              content: toolResult.content,
              tool_call_id: toolResult.tool_call_id,
              tool_name: toolResult.tool_name,
              created_at: Date.now(),
            };
            setMessages(prev => [...prev, toolMessage]);
          });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setLoading(false);
      setStreaming(false);
    }
  };

  const createSession = async () => {
    try {
      const res = await apiClient.createSession(newSessionPlatform, newSessionChatId, newSessionUserId || undefined);
      navigate(`/chat/${res.session_id}`, { replace: true });
      setShowCreateSession(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create session');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  if (showCreateSession || !sessionId) {
    return (
      <div className="max-w-2xl mx-auto mt-12 p-6 bg-white dark:bg-dark-900 rounded-xl border border-gray-200 dark:border-dark-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Create New Session</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Platform</label>
            <select
              value={newSessionPlatform}
              onChange={(e) => setNewSessionPlatform(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            >
              <option value="cli">CLI</option>
              <option value="telegram">Telegram</option>
              <option value="discord">Discord</option>
              <option value="slack">Slack</option>
              <option value="web">Web</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chat ID</label>
            <input
              type="text"
              value={newSessionChatId}
              onChange={(e) => setNewSessionChatId(e.target.value)}
              placeholder="e.g., 123456789"
              className="w-full px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">User ID (optional)</label>
            <input
              type="text"
              value={newSessionUserId}
              onChange={(e) => setNewSessionUserId(e.target.value)}
              placeholder="e.g., 987654321"
              className="w-full px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <Button onClick={createSession} loading={loading} className="w-full">
            Create Session
          </Button>
        </div>
        {error && <p className="mt-4 text-red-600 dark:text-red-400 text-center">{error}</p>}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900">
        <div className="flex items-center gap-3">
          <Bot className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {messages.find(m => m.role === 'user')?.content ? 'Chat' : 'New Conversation'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Session: {sessionId?.slice(0, 8)}...
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {error && (
            <span className="text-sm text-red-600 dark:text-red-400 mr-2">{error}</span>
          )}
          <Button variant="ghost" size="sm" onClick={() => navigate('/sessions')}>
            Back to Sessions
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" ref={messagesEndRef}>
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <Bot className="h-16 w-16 mb-4 opacity-50" />
            <p className="text-lg">Start a conversation</p>
            <p className="text-sm">Type a message below to begin</p>
          </div>
        ) : (
          messages.map((msg, index) => (
            <MessageBubble key={msg.id} message={msg} index={index} />
          ))
        )}
        {streaming && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900">
        <form onSubmit={handleSend} className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message... (Shift+Enter for new line)"
            disabled={loading}
            rows={1}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none min-h-[44px] max-h-48"
          />
          <Button
            type="submit"
            disabled={!input.trim() || loading}
            loading={streaming}
            size="lg"
            className="h-10"
          >
            <Send className="h-5 w-5" />
          </Button>
        </form>
      </div>
    </div>
  );
}

function MessageBubble({ message, index }: { message: Message & { isStreaming?: boolean }; index: number }) {
  const isUser = message.role === 'user';
  const isTool = message.role === 'tool';
  const isAssistant = message.role === 'assistant';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[70%] ${isUser ? 'text-right' : 'text-left'}`}>
        {/* Role indicator */}
        {!isUser && (
          <div className="flex items-center gap-2 mb-1">
            {isAssistant && <Bot className="h-4 w-4 text-primary-600 dark:text-primary-400" />}
            {isTool && <Wrench className="h-4 w-4 text-orange-600 dark:text-orange-400" />}
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 capitalize">
              {message.role}
            </span>
            {message.tool_name && (
              <span className="text-xs text-gray-400 dark:text-gray-500 px-2 py-0.5 bg-gray-100 dark:bg-dark-800 rounded">
                {message.tool_name}
              </span>
            )}
          </div>
        )}

        {/* Message content */}
        <div
          className={`inline-block px-4 py-2 rounded-2xl ${
            isUser
              ? 'bg-primary-600 text-white rounded-br-none'
              : isTool
              ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-900 dark:text-orange-100 rounded-bl-none'
              : 'bg-gray-100 dark:bg-dark-800 text-gray-900 dark:text-white rounded-bl-none'
          }`}
        >
          <pre className="whitespace-pre-wrap text-sm">{message.content || '<empty>'}</pre>
          
          {/* Tool calls */}
          {message.tool_calls && message.tool_calls.length > 0 && (
            <div className="mt-2 space-y-1">
              {message.tool_calls.map((tc, i) => (
                <details key={i} className="text-xs">
                  <summary className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                    🔧 {tc.function.name}
                  </summary>
                  <pre className="mt-1 p-2 bg-gray-200 dark:bg-dark-700 rounded text-gray-900 dark:text-white overflow-x-auto">
                    {tc.function.arguments}
                  </pre>
                </details>
              ))}
            </div>
          )}
        </div>

        {/* Timestamp */}
        <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
          {formatTimestamp(message.created_at)}
        </div>
      </div>
    </div>
  );
}